import type {
  Image,
  ImageInfoDicom,
  Series,
  SeriesInfoDicom,
  Slice,
  StudyInfoDicom,
} from './current-study-store';

// ---------------------------------------------------------------------------
// Public API types
// ---------------------------------------------------------------------------

export interface SortingContext {
  seriesInfoDicom: Record<string, SeriesInfoDicom>;
  imageInfoDicom: Record<string, ImageInfoDicom>;
  seriesImagesDicom: Record<string, string[]>;
  studyInfoDicom: StudyInfoDicom;
}

export interface SortingOptions {
  /** 'desc' preserves current behaviour (highest slice-location first). */
  sliceLocationOrder?: 'asc' | 'desc';
}

export interface SortingResult {
  series: Series[];
  /**
   * Synthetic SeriesInfoDicom entries created during sorting (e.g. Siemens merged UIDs).
   *  The caller should merge these back into seriesInfoDicom.
   */
  newSeriesInfo: Record<string, SeriesInfoDicom>;
  /**
   * Per-image temporal offsets: imageUID → seriesIndex → ms delta.
   *  The caller should write these into imageInfoDicom[uid].CalculatedSliceTime.
   */
  sliceTimes: Record<string, Record<number, number>>;
}

// ---------------------------------------------------------------------------
// StudySorter
// ---------------------------------------------------------------------------

export class StudySorter {
  private readonly ctx: SortingContext;
  private readonly opts: Required<SortingOptions>;

  constructor(ctx: SortingContext, options: SortingOptions = {}) {
    this.ctx = ctx;
    this.opts = { sliceLocationOrder: 'desc', ...options };
  }

  sort(series: Series[]): SortingResult {
    const sorted = this.applyGenericSort(series);
    const manufacturer = (this.ctx.studyInfoDicom.Manufacturer ?? '').toLowerCase();

    let result: SortingResult;
    if (manufacturer.includes('philips')) {
      result = this.applyPhilipsSort(sorted);
    }
    else if (manufacturer.includes('ge')) {
      result = this.applyGESort(sorted);
    }
    else if (manufacturer.includes('siemens')) {
      result = this.applySiemensSort(sorted);
    }
    else {
      result = { series: sorted, newSeriesInfo: {}, sliceTimes: {} };
    }

    result.sliceTimes = this.computeSliceTimes(result.series);
    return result;
  }

  // ---------------------------------------------------------------------------
  // Generic sort: SeriesNumber → slice location → InstanceNumber
  // ---------------------------------------------------------------------------

  private applyGenericSort(series: Series[]): Series[] {
    const { seriesInfoDicom, seriesImagesDicom, imageInfoDicom } = this.ctx;
    const locDir = this.opts.sliceLocationOrder === 'asc' ? 1 : -1;

    const sorted = [...series].sort(
      (a, b) =>
        Number.parseInt(seriesInfoDicom[a.uid]?.SeriesNumber ?? '0', 10)
        - Number.parseInt(seriesInfoDicom[b.uid]?.SeriesNumber ?? '0', 10),
    );

    return sorted.map((s) => {
      const images = (seriesImagesDicom[s.uid] ?? [])
        .map(uid => imageInfoDicom[uid])
        .filter(Boolean);

      const slicesMap: Record<string, Image[]> = {};
      images.forEach((image) => {
        const loc = String(image.CalculatedSliceLocation);
        (slicesMap[loc] ||= []).push({ uid: image.SOPInstanceUID });
      });

      const sortedSlices: Slice[] = Object.keys(slicesMap)
        .sort((a, b) => (Number.parseFloat(a) - Number.parseFloat(b)) * locDir)
        .map((loc) => {
          const imgs = slicesMap[loc];
          imgs.sort(
            (a, b) =>
              (imageInfoDicom[a.uid]?.InstanceNumber ?? 0)
              - (imageInfoDicom[b.uid]?.InstanceNumber ?? 0),
          );
          return { images: imgs };
        });

      return { ...s, slices: sortedSlices };
    });
  }

  // ---------------------------------------------------------------------------
  // Philips: placeholder — implement manufacturer-specific sorting here
  // ---------------------------------------------------------------------------

  private applyPhilipsSort(series: Series[]): SortingResult {
    // TODO: implement Philips-specific sorting
    return { series, newSeriesInfo: {}, sliceTimes: {} };
  }

  // ---------------------------------------------------------------------------
  // GE:
  //   Pass 1 — within each slice, group images by InstanceNumber then sort
  //            each group by TriggerTime ascending and flatten.
  //   Pass 2 — for single-slice VENC series, split into phase / magnitude slices.
  // ---------------------------------------------------------------------------

  private applyGESort(series: Series[]): SortingResult {
    const { imageInfoDicom } = this.ctx;

    // Pass 1: group by InstanceNumber, sort by TriggerTime, flatten
    series.forEach((s) => {
      s.slices.forEach((slice) => {
        const byInstance: Record<number, Image[]> = {};
        slice.images.forEach((img) => {
          const n = imageInfoDicom[img.uid]?.InstanceNumber ?? 0;
          (byInstance[n] ||= []).push(img);
        });

        const sortedImages: Image[] = [];
        Object.keys(byInstance)
          .map(Number)
          .sort((a, b) => a - b)
          .forEach((n) => {
            byInstance[n].sort(
              (a, b) =>
                (imageInfoDicom[a.uid]?.TriggerTime ?? 0)
                - (imageInfoDicom[b.uid]?.TriggerTime ?? 0),
            );
            sortedImages.push(...byInstance[n]);
          });

        slice.images = sortedImages;
      });
    });

    // Pass 2: split single-slice VENC series into venc (phase) / non-venc (magnitude)
    const result = series.map((s) => {
      if (s.slices.length !== 1) return s;

      const slice = s.slices[0];
      const firstInfo = slice.images[0]
        ? imageInfoDicom[slice.images[0].uid]
        : undefined;
      if (!firstInfo?.IsVencImage2D) return s;

      const vencImages: Image[] = [];
      const nonVencImages: Image[] = [];
      slice.images.forEach((img) => {
        if (imageInfoDicom[img.uid]?.IsVencImage2D) {
          vencImages.push(img);
        }
        else {
          nonVencImages.push(img);
        }
      });

      const byTriggerTime = (a: Image, b: Image) =>
        (imageInfoDicom[a.uid]?.TriggerTime ?? 0)
        - (imageInfoDicom[b.uid]?.TriggerTime ?? 0);

      vencImages.sort(byTriggerTime);
      nonVencImages.sort(byTriggerTime);

      return { ...s, slices: [{ images: vencImages }, { images: nonVencImages }] };
    });

    return { series: result, newSeriesInfo: {}, sliceTimes: {} };
  }

  // ---------------------------------------------------------------------------
  // Siemens: merge consecutive same-description series, split by ImageComments,
  //          then fold single-location VENC series into their predecessor.
  // ---------------------------------------------------------------------------

  private applySiemensSort(series: Series[]): SortingResult {
    const newSeriesInfo: Record<string, SeriesInfoDicom> = {};
    const merged = this.siemensMergeAndSplit(series, newSeriesInfo);
    const withVenc = this.siemensMergeVenc(merged, newSeriesInfo);
    return { series: withVenc, newSeriesInfo, sliceTimes: {} };
  }

  private siemensMergeAndSplit(
    series: Series[],
    newSeriesInfo: Record<string, SeriesInfoDicom>,
  ): Series[] {
    const { seriesInfoDicom, imageInfoDicom } = this.ctx;

    interface Group {
      description: string;
      baseUid: string;
      slicesByLocation: Record<string, Image[]>;
    }

    const mergedSeries: Series[] = [];
    let currentGroup: Group | null = null;

    const toDT = (v: unknown): number => {
      if (!v) return Number.POSITIVE_INFINITY;
      const digits = String(v).replace(/\D/g, '');
      return digits ? Number(digits) : Number.POSITIVE_INFINITY;
    };

    const flushGroup = () => {
      if (!currentGroup) return;

      // 1. Partition all images by ImageComments key
      const byComments: Record<string, Image[]> = {};
      Object.values(currentGroup.slicesByLocation).forEach((imgs) => {
        imgs.forEach((img) => {
          let key = imageInfoDicom[img.uid]?.ImageComments ?? '';
          if (!key.includes('CineVN') && !key.includes('CS')) key = '';
          (byComments[key] ||= []).push(img);
        });
      });

      // 2. For each comments partition, build a series (keep base UID)
      Object.keys(byComments).forEach((key) => {
        const byLoc: Record<string, Image[]> = {};
        byComments[key].forEach((img) => {
          const loc = String(imageInfoDicom[img.uid]?.CalculatedSliceLocation);
          (byLoc[loc] ||= []).push(img);
        });

        const slices: Slice[] = Object.keys(byLoc)
          .sort((a, b) => Number.parseFloat(b) - Number.parseFloat(a))
          .map((loc) => {
            const images = byLoc[loc];
            images.sort((a, b) => {
              const A = imageInfoDicom[a.uid];
              const B = imageInfoDicom[b.uid];
              const aDT = toDT(A?.AcquisitionDateTime);
              const bDT = toDT(B?.AcquisitionDateTime);
              if (aDT !== bDT) return aDT - bDT;
              return (A?.InstanceNumber ?? 0) - (B?.InstanceNumber ?? 0);
            });
            return { images };
          });

        const { baseUid } = currentGroup!;
        const newUid = baseUid;
        const baseInfo = seriesInfoDicom[baseUid];

        if (baseInfo && !seriesInfoDicom[newUid] && !newSeriesInfo[newUid]) {
          const tags: string[] = [];
          if (key.includes('CineVN')) tags.push('CineVN');
          if (key.includes('CS')) tags.push('CS');
          const seriesDesc = [baseInfo.SeriesDescription, ...tags]
            .filter(Boolean)
            .join(' ')
            .trim();

          newSeriesInfo[newUid] = {
            OrthancID: baseInfo.OrthancID,
            SeriesInstanceUID: newUid,
            SeriesNumber: baseInfo.SeriesNumber,
            SeriesDescription: seriesDesc,
          };
        }

        mergedSeries.push({ uid: newUid, slices });
      });
    };

    series.forEach((s) => {
      const description = (
        seriesInfoDicom[s.uid]?.SeriesDescription ?? ''
      ).trim().toLowerCase();

      if (
        !currentGroup
        || currentGroup.description !== description
        || description.includes('os')
      ) {
        flushGroup();
        currentGroup = { description, baseUid: s.uid, slicesByLocation: {} };
      }

      s.slices.forEach((slice) => {
        if (!slice.images.length) return;
        const loc = String(
          imageInfoDicom[slice.images[0].uid]?.CalculatedSliceLocation,
        );
        (currentGroup!.slicesByLocation[loc] ||= []).push(...slice.images);
      });
    });

    flushGroup();
    return mergedSeries;
  }

  private siemensMergeVenc(
    series: Series[],
    newSeriesInfo: Record<string, SeriesInfoDicom>,
  ): Series[] {
    const { imageInfoDicom, seriesInfoDicom } = this.ctx;
    const result: Series[] = [];

    const uniqueLocations = (s: Series): Set<unknown> =>
      new Set(
        s.slices
          .flatMap(sl => sl.images.slice(0, 1))
          .map(img => imageInfoDicom[img.uid]?.CalculatedSliceLocation)
          .filter(v => v != null),
      );

    const imageCount = (s: Series) =>
      s.slices.reduce((n, sl) => n + sl.images.length, 0);

    for (const current of series) {
      if (current.slices.length === 0 || current.slices[0].images.length === 0) {
        result.push(current);
        continue;
      }

      const firstInfo = imageInfoDicom[current.slices[0].images[0].uid];
      const isVenc = firstInfo?.IsVencImage2D === true;
      const currentLocs = uniqueLocations(current);

      if (isVenc && currentLocs.size === 1 && result.length > 0) {
        const prev = result[result.length - 1];
        const prevLocs = uniqueLocations(prev);

        const locMatch
          = prevLocs.size === 1
            && Array.from(currentLocs)[0] === Array.from(prevLocs)[0];
        const countMatch = imageCount(current) === imageCount(prev);

        if (locMatch && countMatch) {
          console.log('Merging VENC series with previous series');

          const vencSlices = current.slices.map(sl => ({ images: [...sl.images] }));
          prev.slices.unshift(...vencSlices);

          const infoTarget = newSeriesInfo[prev.uid] ?? seriesInfoDicom[prev.uid];
          if (infoTarget && !infoTarget.SeriesDescription.includes('VENC')) {
            infoTarget.SeriesDescription
              = `${infoTarget.SeriesDescription} VENC: ${firstInfo.Venc2D}`;
          }
          continue;
        }
      }

      result.push(current);
    }

    return result;
  }

  // ---------------------------------------------------------------------------
  // Timestamp annotation
  // Per-image temporal offset relative to the first image in its slice.
  // Priority: AcquisitionDateTime > AcquisitionDate+AcquisitionTime > AcquisitionTime
  // ---------------------------------------------------------------------------

  computeSliceTimes(series: Series[]): Record<string, Record<number, number>> {
    const { imageInfoDicom } = this.ctx;
    const result: Record<string, Record<number, number>> = {};

    series.forEach((s, seriesIndex) => {
      s.slices.forEach((slice) => {
        if (!slice.images.length) return;

        const firstTS = this.resolveTimestamp(imageInfoDicom[slice.images[0].uid]);

        slice.images.forEach((image) => {
          const info = imageInfoDicom[image.uid];
          if (!info) return;

          const ts = this.resolveTimestamp(info);
          const delta = ts && firstTS ? ts.ms - firstTS.ms : 0;

          if (!result[image.uid]) result[image.uid] = {};
          result[image.uid][seriesIndex] = delta;
        });
      });
    });

    return result;
  }

  private resolveTimestamp(
    info: ImageInfoDicom | undefined,
  ): { ms: number; hasDate: boolean } | null {
    if (!info) return null;

    if (info.AcquisitionDateTime) {
      const dt = info.AcquisitionDateTime.replace(/\D/g, '');
      if (dt.length >= 14) {
        const dateMs = this.parseDateMs(dt.substring(0, 8));
        const timeMs = this.parseTimeMs(dt.substring(8));
        if (dateMs !== null && timeMs !== null)
          return { ms: dateMs + timeMs, hasDate: true };
      }
    }
    if (info.AcquisitionDate && info.AcquisitionTime) {
      const dateMs = this.parseDateMs(info.AcquisitionDate);
      const timeMs = this.parseTimeMs(info.AcquisitionTime);
      if (dateMs !== null && timeMs !== null)
        return { ms: dateMs + timeMs, hasDate: true };
    }
    if (info.AcquisitionTime) {
      const timeMs = this.parseTimeMs(info.AcquisitionTime);
      if (timeMs !== null) return { ms: timeMs, hasDate: false };
    }
    return null;
  }

  private parseTimeMs(timeStr: string): number | null {
    const d = timeStr.replace(/\D/g, '').padEnd(14, '0');
    const h = Number.parseInt(d.substring(0, 2), 10);
    const m = Number.parseInt(d.substring(2, 4), 10);
    const s = Number.parseInt(d.substring(4, 6), 10);
    const us = Number.parseInt(d.substring(6, 14), 10);
    if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return null;
    return (h * 3600 + m * 60 + s) * 1000 + Math.round(us / 1e5);
  }

  private parseDateMs(dateStr: string): number | null {
    const d = dateStr.replace(/\D/g, '');
    if (d.length < 8) return null;
    const date = new Date(
      Number.parseInt(d.substring(0, 4), 10),
      Number.parseInt(d.substring(4, 6), 10) - 1,
      Number.parseInt(d.substring(6, 8), 10),
    );
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }
}
